<template>
  <div class="device-license-list">
    <div class="page-header">
      <div class="page-header-left">
        <div class="page-title">设备License数量管理</div>
        <div class="page-desc">
          管理普通租户可使用的设备License数量上限，超过将限制新增设备
        </div>
      </div>
      <a-button type="primary" icon="plus" @click="toAdd">
        新增License限制
      </a-button>
    </div>

    <a-row v-show="statVisible" :gutter="16" class="stat-row">
      <a-col :span="6" v-for="item in statCards" :key="item.key">
        <div class="stat-card">
          <div class="stat-label">{{ item.label }}</div>
          <div class="stat-value" :style="{ color: item.valueColor || '#151515' }">
            {{ item.value }}
          </div>
          <div class="stat-extra" :style="{ color: item.extraColor || '#8c8c8c' }">
            <span v-if="item.trendStatus === 'up'">↑ </span>
            <span v-else-if="item.trendStatus === 'down'">↓ </span>
            <!-- <span v-else-if="item.trendStatus === 'flat'">— </span> -->
            {{ item.extra }}
          </div>
        </div>
      </a-col>
    </a-row>

    <div class="content-card">
      <a-form layout="inline" :form="form" class="filter-form">
        <a-form-item>
          <a-select
            show-search
            allow-clear
            placeholder="选择租户"
            style="width: 200px"
            :filter-option="false"
            :not-found-content="tenantLoading ? undefined : null"
            :loading="tenantLoading"
            @search="handleTenantSearch"
            @focus="handleTenantFocus"
            v-decorator="['tenantId']"
          >
            <a-select-option
              v-for="item in tenantOptions"
              :key="item.id"
              :value="item.id"
            >
              {{ item.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-select
            allow-clear
            placeholder="全部状态"
            style="width: 160px"
            v-decorator="['status']"
          >
            <a-select-option value="">全部状态</a-select-option>
            <a-select-option value="EFFECTIVE">生效中</a-select-option>
            <a-select-option value="EXPIRING_SOON">即将到期</a-select-option>
            <a-select-option value="USED_UP">已用完</a-select-option>
            <a-select-option value="EXPIRED">已过期</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-range-picker
            style="width: 280px"
            v-decorator="['dateRange']"
            :placeholder="['开始日期', '结束日期']"
          />
        </a-form-item>
        <a-form-item>
          <a-button @click="handleReset">重置</a-button>
          <a-button type="primary" style="margin-left: 8px" @click="handleSearch">
            查询
          </a-button>
          <a-tooltip :title="statVisible ? '收起看板' : '展开看板'">
            <a-button
              style="margin-left: 8px"
              :icon="statVisible ? 'up' : 'down'"
              @click="toggleStat"
            />
          </a-tooltip>
        </a-form-item>
      </a-form>

      <a-table
        ref="table"
        :columns="columns"
        :data-source="dataList"
        :pagination="pagination"
        :loading="loading"
        :rowKey="(record) => record.id"
        :scroll="{ x: 1400, y: scrollY }"
        @change="tableChange"
      >
        <template slot="index" slot-scope="text">
          {{ String(text).padStart(2, '0') }}
        </template>
        <template slot="tenantName" slot-scope="text, record">
          <div class="tenant-name-cell">
            <a-avatar
              :size="28"
              :style="{ backgroundColor: record.avatarColor, marginRight: '8px' }"
            >
              {{ record.avatarText }}
            </a-avatar>
            <span>{{ text || '--' }}</span>
          </div>
        </template>
        <template slot="quota" slot-scope="text">{{ text }} 台</template>
        <template slot="used" slot-scope="text">{{ text }} 台</template>
        <template slot="remain" slot-scope="text">
          <span :style="{ color: getRemainColor(text) }">{{ text }} 台</span>
        </template>
        <template slot="validPeriod" slot-scope="text, record">
          {{ formatDate(record.startDate) }} 至 {{ formatDate(record.endDate) }}
        </template>
        <template slot="status" slot-scope="text">
          <a-badge
            :status="(statusMap[text] || statusMap.EXPIRED).badge"
            :text="(statusMap[text] || statusMap.EXPIRED).text"
          />
        </template>
        <template slot="action" slot-scope="text, record">
          <a @click="toDetail(record, 'view')">查看</a>
          <a-divider type="vertical" />
          <a @click="toDetail(record, 'edit')">编辑</a>
          <a-divider type="vertical" />
          <a-popconfirm
            title="确定删除该License限制吗？"
            ok-text="确定"
            cancel-text="取消"
            @confirm="handleDelete(record)"
          >
            <a style="color: #ff4d4f">删除</a>
          </a-popconfirm>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script>
const AVATAR_COLORS = [
  '#1890ff',
  '#52c41a',
  '#722ed1',
  '#fa8c16',
  '#13c2c2',
  '#eb2f96',
  '#2f54eb',
]

const columns = [
  {
    title: '序号',
    dataIndex: 'index',
    key: 'index',
    width: 70,
    scopedSlots: { customRender: 'index' },
  },
  {
    title: '租户ID',
    dataIndex: 'tenantId',
    key: 'tenantId',
    width: 180,
  },
  {
    title: '租户名称',
    dataIndex: 'tenantName',
    key: 'tenantName',
    scopedSlots: { customRender: 'tenantName' },
  },
  {
    title: 'License配额',
    dataIndex: 'quota',
    key: 'quota',
    scopedSlots: { customRender: 'quota' },
    width: 120,
  },
  {
    title: '已使用',
    dataIndex: 'used',
    key: 'used',
    scopedSlots: { customRender: 'used' },
    width: 120,
  },
  {
    title: '剩余',
    dataIndex: 'remain',
    key: 'remain',
    scopedSlots: { customRender: 'remain' },
    width: 120,
  },
  {
    title: '有效期',
    dataIndex: 'validPeriod',
    key: 'validPeriod',
    width: 220,
    scopedSlots: { customRender: 'validPeriod' },
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    scopedSlots: { customRender: 'status' },
  },
  {
    title: '操作',
    dataIndex: 'action',
    key: 'action',
    width: 160,
    fixed: 'right',
    scopedSlots: { customRender: 'action' },
  },
]

export default {
  name: 'DeviceLicenseList',
  data() {
    return {
      form: this.$form.createForm(this),
      columns,
      dataList: [],
      tenantOptions: [],
      tenantLoading: false,
      loading: false,
      _lastTenantKey: undefined,
      statusMap: {
        EFFECTIVE: { text: '生效中', badge: 'success' },
        EXPIRING_SOON: { text: '即将到期', badge: 'warning' },
        USED_UP: { text: '已用完', badge: 'error' },
        EXPIRED: { text: '已过期', badge: 'default' },
      },
      statCards: [
        {
          key: 'totalQuota',
          label: 'License配额总数',
          value: '-',
          extra: '--',
          extraColor: '#8c8c8c',
          trendStatus: 'flat',
        },
        {
          key: 'tenantCount',
          label: '配额租户数',
          value: '-',
          extra: '--',
          extraColor: '#8c8c8c',
          trendStatus: 'flat',
        },
        {
          key: 'used',
          label: '已使用',
          value: '-',
          valueColor: '#1890ff',
          extra: '使用率 --',
          extraColor: '#8c8c8c',
        },
        {
          key: 'remain',
          label: '剩余',
          value: '-',
          valueColor: '#52c41a',
          extra: '剩余率 --',
          extraColor: '#8c8c8c',
        },
      ],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showTotal: (total) => `共 ${total} 条`,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['10', '20', '30'],
      },
      scrollY: 400,
      statVisible: true,
    }
  },
  created() {
    this.fetchTenantOptions()
    this.fetchStatistics()
    this.fetchList()
  },
  mounted() {
    setTimeout(() => {
      this.calcTableScrollY()
    }, 0)
    window.addEventListener('resize', this.calcTableScrollY)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calcTableScrollY)
  },
  methods: {
    formatDate(val) {
      if (!val) return '--'
      return this.$moment(val).format('YYYY-MM-DD')
    },
    formatNumber(val) {
      if (val === null || val === undefined || val === '') return '-'
      return Number(val).toLocaleString()
    },
    formatPercent(val) {
      if (val === null || val === undefined || val === '') return '--'
      return `${Number(val).toFixed(1)}%`
    },
    formatTrendPercent(val) {
      if (val === null || val === undefined || val === '') return '--'
      const n = Number(val)
      if (Number.isNaN(n)) return '--'
      const sign = n >= 0 ? '+' : '-'
      return `较上月 ${sign}${Math.abs(n)}%`
    },
    formatTrendCount(val) {
      if (val === null || val === undefined || val === '') return '--'
      const n = Number(val)
      if (Number.isNaN(n)) return '--'
      const sign = n >= 0 ? '+' : '-'
      return `较上月 ${sign}${Math.abs(n)} 个`
    },
    getTrendColor(val) {
      const n = Number(val)
      if (Number.isNaN(n) || n === 0) return '#8c8c8c'
      return n > 0 ? '#52c41a' : '#ff4d4f'
    },
    // up 正 / flat 平 / down 负
    getTrendStatus(val) {
      const n = Number(val)
      if (val === null || val === undefined || val === '' || Number.isNaN(n) || n === 0) {
        return 'flat'
      }
      return n > 0 ? 'up' : 'down'
    },
    getAvatarMeta(name, id) {
      const text = name ? String(name).charAt(0) : '?'
      const seed = String(id || name || '')
      let hash = 0
      for (let i = 0; i < seed.length; i++) {
        hash = (hash + seed.charCodeAt(i)) % AVATAR_COLORS.length
      }
      return {
        avatarText: text,
        avatarColor: AVATAR_COLORS[hash],
      }
    },
    calcTableScrollY() {
      this.$nextTick(() => {
        const tableEl = this.$refs.table && this.$refs.table.$el
        if (!tableEl) return
        const top = tableEl.getBoundingClientRect().top + 64 + 34
        const pagiH = 32
        const pageBottom = 2
        const bottomReserve = pageBottom + pagiH
        this.scrollY = Math.max(window.innerHeight - top - bottomReserve, 200)
      })
    },
    toggleStat() {
      this.statVisible = !this.statVisible
      this.calcTableScrollY()
    },
    getRemainColor(remain) {
      if (remain <= 0) return '#ff4d4f'
      if (remain <= 10) return '#fa8c16'
      return '#52c41a'
    },
    handleTenantSearch(value) {
      const keyword = (value || '').trim()
      if (this._tenantSearchTimer) {
        clearTimeout(this._tenantSearchTimer)
      }
      this._tenantSearchTimer = setTimeout(() => {
        // 与上次检索条件相同则跳过，避免打开弹层时空 search 重复请求导致闪烁
        if (keyword === this._lastTenantKey) return
        this.fetchTenantOptions(keyword)
      }, 300)
    },
    handleTenantFocus() {
      if (!this.tenantOptions.length && !this.tenantLoading) {
        this.fetchTenantOptions()
      }
    },
    // 备注：租户下拉无专用 License 接口，复用平台租户列表；key 支持租户ID精确或租户名模糊
    async fetchTenantOptions(key) {
      this.tenantLoading = true
      try {
        const params = { page: 1, size: 20 }
        const keyword = (key || '').trim()
        this._lastTenantKey = keyword
        if (keyword) params.key = keyword
        const res = await this.$api._getTenantInfoList(params)
        if (res.code == 0) {
          const records = (res.data && res.data.content) || []
          this.tenantOptions = records.map((item) => ({
            id: String(item.id),
            name: item.name,
          }))
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.tenantLoading = false
      }
    },
    async fetchStatistics() {
      try {
        const res = await this.$api.getDeviceLicenseStatistics()
        if (res.code !== 0) {
          this.$message.error(res.message || '获取统计数据失败')
          return
        }
        const data = res.data || {}
        this.statCards = [
          {
            key: 'totalQuota',
            label: 'License配额总数',
            value: this.formatNumber(data.totalQuota),
            extra: this.formatTrendPercent(data.totalQuotaTrend),
            extraColor: this.getTrendColor(data.totalQuotaTrend),
            trendStatus: this.getTrendStatus(data.totalQuotaTrend),
          },
          {
            key: 'tenantCount',
            label: '配额租户数',
            value: this.formatNumber(data.tenantCount),
            extra: this.formatTrendCount(data.tenantCountTrend),
            extraColor: this.getTrendColor(data.tenantCountTrend),
            trendStatus: this.getTrendStatus(data.tenantCountTrend),
          },
          {
            key: 'used',
            label: '已使用',
            value: this.formatNumber(data.totalUsed),
            valueColor: '#1890ff',
            // 备注：卡片副文案使用 usedRate；接口另有 totalUsedTrend 未在此展示
            extra: `使用率 ${this.formatPercent(data.usedRate)}`,
            extraColor: '#8c8c8c',
          },
          {
            key: 'remain',
            label: '剩余',
            value: this.formatNumber(data.totalRemain),
            valueColor: '#52c41a',
            // 备注：卡片副文案使用 remainRate；接口另有 totalRemainTrend 未在此展示
            extra: `剩余率 ${this.formatPercent(data.remainRate)}`,
            extraColor: '#8c8c8c',
          },
        ]
      } catch (e) {
        console.error(e)
      }
    },
    buildQueryParams() {
      const values = this.form.getFieldsValue()
      const params = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
      }
      if (values.tenantId) params.tenantId = values.tenantId
      if (values.status) params.status = values.status
      if (values.dateRange && values.dateRange.length === 2) {
        params.startDate = values.dateRange[0].format('YYYY-MM-DD')
        params.endDate = values.dateRange[1].format('YYYY-MM-DD')
      }
      return params
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await this.$api.getDeviceLicensePage(this.buildQueryParams())
        if (res.code !== 0) {
          this.$message.error(res.message || '获取列表失败')
          return
        }
        const pageData = res.data || {}
        const records = pageData.records || []
        this.pagination.total = Number(pageData.total || 0)
        this.dataList = records.map((item, index) => {
          const avatar = this.getAvatarMeta(item.tenantName, item.tenantId)
          return {
            ...item,
            id: String(item.id),
            tenantId: String(item.tenantId),
            index:
              (this.pagination.current - 1) * this.pagination.pageSize + index + 1,
            ...avatar,
          }
        })
        this.$nextTick(() => {
          this.calcTableScrollY()
        })
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.fetchList()
    },
    handleReset() {
      this.form.resetFields()
      this.pagination.current = 1
      this.fetchList()
    },
    tableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.fetchList()
    },
    toAdd() {
      this.$router.push({ name: 'DeviceLicenseAdd' })
    },
    toDetail(record, mode) {
      this.$router.push({
        name: 'DeviceLicenseDetail',
        query: { id: record.id, mode },
      })
    },
    async handleDelete(record) {
      try {
        const res = await this.$api.deleteDeviceLicense(record.id)
        if (res.code == 0) {
          this.$message.success('删除成功')
          if (this.dataList.length === 1 && this.pagination.current > 1) {
            this.pagination.current -= 1
          }
          this.fetchList()
          this.fetchStatistics()
        } else {
          this.$message.error(res.message || '删除失败')
        }
      } catch (e) {
        console.error(e)
      }
    },
  },
}
</script>

<style lang="less" scoped>
.device-license-list {
  padding: 16px 16px 0 16px;
  min-height: 100%;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #151515;
  line-height: 28px;
}

.page-desc {
  margin-top: 4px;
  font-size: 13px;
  color: #8c8c8c;
  line-height: 20px;
}

.stat-row {
  margin-bottom: 16px;
}

.stat-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 16px 20px;
}

.stat-label {
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 26px;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 6px;
}

.stat-extra {
  font-size: 11px;
  line-height: 1;
}

.content-card {
  background: #fff;
}

.filter-form {
  margin-bottom: 16px;
}

.tenant-name-cell {
  display: flex;
  align-items: center;
}
</style>
